import type {
  PromoteResult,
  PromotionRecord,
  StagePosition,
  BackPromoteFanOutResult,
  BackPromoteCascadeResult,
} from '../../types/promotion.types'
import type { IPromotionService, PromoteBody, BackPromoteFanOutBody, BackPromoteCascadeBody } from './IPromotionService.interface'
import { authHeaders } from '../../lib/apiClient'

// ── API base URL ──────────────────────────────────────────────────────────────
// VITE_API_BASE_URL is injected by Vite at build time.
// Falls back to localhost for local development.

const BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001'

// ── Typed fetch helper ────────────────────────────────────────────────────────

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()), ...(init?.headers ?? {}) },
  })
  if (!res.ok) {
    const body: unknown = await res.json().catch(() => ({}))
    const message = (body as { message?: string }).message ?? `API error ${res.status}`
    throw new Error(message)
  }
  return res.json() as Promise<T>
}

// ── HttpPromotionService ──────────────────────────────────────────────────────

export class HttpPromotionService implements IPromotionService {
  /**
   * A 409 carrying `{ needsAck:true, drift }` is the drift gate — NOT an error.
   * Return the parsed body as a valid PromoteResult. All other non-OK statuses throw.
   */
  promote(slug: string, body: PromoteBody): Promise<PromoteResult> {
    return this._postPromote(`/api/pipelines/${encodeURIComponent(slug)}/promote`, body)
  }

  backPromote(slug: string, body: PromoteBody): Promise<PromoteResult> {
    return this._postPromote(`/api/pipelines/${encodeURIComponent(slug)}/back-promote`, body)
  }

  backPromoteFanOut(slug: string, body: BackPromoteFanOutBody): Promise<BackPromoteFanOutResult> {
    return apiFetch<BackPromoteFanOutResult>(
      `/api/pipelines/${encodeURIComponent(slug)}/back-promote/fanout`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  }

  backPromoteCascade(slug: string, body: BackPromoteCascadeBody): Promise<BackPromoteCascadeResult> {
    return apiFetch<BackPromoteCascadeResult>(
      `/api/pipelines/${encodeURIComponent(slug)}/back-promote/cascade`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  }

  /** Shared POST for promote / back-promote — a 409 `{needsAck,drift}` is the drift gate, not an error. */
  private async _postPromote(path: string, body: PromoteBody): Promise<PromoteResult> {
    const res = await fetch(`${BASE}${path}`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      body:    JSON.stringify(body),
    })

    if (res.status === 409) {
      const parsed: unknown = await res.json().catch(() => ({}))
      if ((parsed as { needsAck?: boolean }).needsAck === true) {
        return parsed as PromoteResult
      }
      // Some other 409 (real conflict) — fall through to the error path.
      const message = (parsed as { message?: string }).message ?? `API error ${res.status}`
      throw new Error(message)
    }

    if (!res.ok) {
      const errBody: unknown = await res.json().catch(() => ({}))
      const message = (errBody as { message?: string }).message ?? `API error ${res.status}`
      throw new Error(message)
    }

    return res.json() as Promise<PromoteResult>
  }

  merge(slug: string, promotionId: string): Promise<{ merged: boolean; deployRunId: string; record: PromotionRecord }> {
    return apiFetch<{ merged: boolean; deployRunId: string; record: PromotionRecord }>(
      `/api/pipelines/${encodeURIComponent(slug)}/promotions/${encodeURIComponent(promotionId)}/merge`,
      { method: 'POST', body: JSON.stringify({}) },
    )
  }

  listPromotions(slug: string): Promise<PromotionRecord[]> {
    return apiFetch<PromotionRecord[]>(`/api/pipelines/${encodeURIComponent(slug)}/promotions`)
  }

  positions(slug: string): Promise<StagePosition[]> {
    return apiFetch<StagePosition[]>(`/api/pipelines/${encodeURIComponent(slug)}/positions`)
  }
}
